import React, { useState } from 'react';
import { authService } from '../services/authService';
import { API_CONFIG } from '../utils/config';
import ImprintScreen from './ImprintScreen';
import '../styles/FormControls.css';
import './LoginScreen.css';

interface LoginScreenProps {
  onLoginSuccess: () => void;
}

function LoginScreen({ onLoginSuccess }: LoginScreenProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  
  // Forgot password state
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  // Forgot username state
  const [showForgotUsernameDialog, setShowForgotUsernameDialog] = useState(false);
  const [forgotUsernameEmail, setForgotUsernameEmail] = useState('');
  const [forgotUsernameError, setForgotUsernameError] = useState('');
  const [forgotUsernameSuccess, setForgotUsernameSuccess] = useState('');
  const [isSendingUsername, setIsSendingUsername] = useState(false);
  
  // Forgot options state
  const [showForgotOptionsDialog, setShowForgotOptionsDialog] = useState(false);
  
  // Imprint screen
  const [showImprintScreen, setShowImprintScreen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authService.login({ username, password });
      authService.setToken(response.token);
      onLoginSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  if (showImprintScreen) {
    return <ImprintScreen onBack={() => setShowImprintScreen(false)} />;
  }

  return (
    <div className="login-screen">
      <div className="login-container">
        <h1 className="login-title">Mochi Vocab</h1>
        <p className="login-subtitle">Sign in to continue</p>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username
            </label>
            <input
              type="text"
              id="username"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <button type="submit" className="btn btn-primary login-button" disabled={isLoading} style={{ maxWidth: 'unset' }}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
          <button 
          type="button" 
          className="btn btn-secondary login-button" 
          onClick={() => setShowRegisterDialog(true)}
          disabled={isLoading}
          style={{ maxWidth: 'unset' }}
        >
          Register
        </button>
          
        </form>
        

        <div className="login-footer" style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="back-button"
            onClick={() => setShowForgotOptionsDialog(true)}
            disabled={isLoading}
          >
            Forgot Password or Username?
          </button>
          <button
            type="button"
            className="back-button"
            onClick={() => setShowImprintScreen(true)}
            disabled={isLoading}
          >
            Impressum
          </button>
        </div>
      </div>

      {/* Registration Dialog */}
      {showRegisterDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowRegisterDialog(false);
            setRegisterUsername('');
            setRegisterPassword('');
            setRegisterConfirmPassword('');
            setRegisterEmail('');
            setRegisterError('');
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Create Account</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="registerUsername" className="form-label">
                  Username
                </label>
                <input
                  type="text"
                  id="registerUsername"
                  className="form-input"
                  value={registerUsername}
                  onChange={(e) => {
                    setRegisterUsername(e.target.value);
                    setRegisterError('');
                  }}
                  disabled={isRegistering}
                  placeholder="5-30 characters, letters, numbers, _ and -"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="registerPassword" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="registerPassword"
                  className="form-input"
                  value={registerPassword}
                  onChange={(e) => {
                    setRegisterPassword(e.target.value);
                    setRegisterError('');
                  }}
                  disabled={isRegistering}
                  placeholder="12-80 characters"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="registerConfirmPassword" className="form-label">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="registerConfirmPassword"
                  className="form-input"
                  value={registerConfirmPassword}
                  onChange={(e) => {
                    setRegisterConfirmPassword(e.target.value);
                    setRegisterError('');
                  }}
                  disabled={isRegistering}
                  placeholder="Confirm password"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="registerEmail" className="form-label">
                  Email (optional)
                </label>
                <input
                  type="email"
                  id="registerEmail"
                  className="form-input"
                  value={registerEmail}
                  onChange={(e) => {
                    setRegisterEmail(e.target.value);
                    setRegisterError('');
                  }}
                  disabled={isRegistering}
                  placeholder="To reset your password, if needed"
                />
              </div>
              {registerError && (
                <div className="error-message" style={{ marginTop: 0 }}>
                  {registerError}
                </div>
              )}
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRegisterDialog(false);
                    setRegisterUsername('');
                    setRegisterPassword('');
                    setRegisterConfirmPassword('');
                    setRegisterEmail('');
                    setRegisterError('');
                  }}
                  disabled={isRegistering}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!registerUsername || !registerPassword || !registerConfirmPassword) {
                      setRegisterError('Please fill in all required fields');
                      return;
                    }
                    if (registerUsername.length < 5 || registerUsername.length > 30) {
                      setRegisterError('Username must be between 5 and 30 characters');
                      return;
                    }
                    if (!/^[a-zA-Z0-9_-]+$/.test(registerUsername)) {
                      setRegisterError('Username can only contain letters, numbers, underscores, and hyphens');
                      return;
                    }
                    if (registerPassword.length < 12 || registerPassword.length > 80) {
                      setRegisterError('Password must be between 12 and 80 characters');
                      return;
                    }
                    if (registerPassword !== registerConfirmPassword) {
                      setRegisterError('Passwords do not match');
                      return;
                    }
                    if (registerEmail && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(registerEmail)) {
                      setRegisterError('Please enter a valid email address');
                      return;
                    }
                    setIsRegistering(true);
                    setRegisterError('');
                    try {
                      const payload: Record<string, string> = {
                        username: registerUsername,
                        password: registerPassword,
                      };
                      if (registerEmail.trim()) {
                        payload.email = registerEmail.trim();
                      }
                      const response = await fetch(`${API_CONFIG.backendUrl}/api/auth/register`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                      });
                      if (!response.ok) {
                        const errorText = await response.text();
                        setRegisterError(errorText || 'Registration failed');
                        return;
                      }
                      // Success - close dialog and show success message
                      setShowRegisterDialog(false);
                      setRegisterUsername('');
                      setRegisterPassword('');
                      setRegisterConfirmPassword('');
                      setRegisterEmail('');
                      setRegisterError('');
                      setSuccess('Account created successfully! Please sign in.');
                    } catch (err) {
                      setRegisterError(err instanceof Error ? err.message : 'Registration failed');
                    } finally {
                      setIsRegistering(false);
                    }
                  }}
                  disabled={isRegistering}
                >
                  {isRegistering ? 'Registering...' : 'Register'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Options Dialog */}
      {showForgotOptionsDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowForgotOptionsDialog(false);
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Need help?</h3>
            <p className="confirm-message">Choose what you need assistance with.</p>
            <div className="confirm-actions" style={{ flexDirection: 'column', gap: '12px', alignItems: 'stretch' }}>
              <button
                className="btn btn-danger"
                onClick={() => {
                  setShowForgotOptionsDialog(false);
                  setShowForgotPasswordDialog(true);
                  setForgotPasswordEmail('');
                  setForgotPasswordError('');
                }}
              >
                Reset Password
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  setShowForgotOptionsDialog(false);
                  setShowForgotUsernameDialog(true);
                  setForgotUsernameEmail('');
                  setForgotUsernameError('');
                  setForgotUsernameSuccess('');
                }}
              >
                Retrieve Username
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowForgotOptionsDialog(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Dialog - Email Input */}
      {showForgotPasswordDialog && !showOtpDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowForgotPasswordDialog(false);
            setForgotPasswordEmail('');
            setForgotPasswordError('');
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Forgot Password</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="forgotPasswordEmail" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="forgotPasswordEmail"
                  className="form-input"
                  value={forgotPasswordEmail}
                  onChange={(e) => {
                    setForgotPasswordEmail(e.target.value);
                    setForgotPasswordError('');
                  }}
                  disabled={isSendingOtp}
                  placeholder="Enter your email address"
                  required
                />
              </div>
              {forgotPasswordError && (
                <div className="error-message" style={{ marginTop: 0 }}>
                  {forgotPasswordError}
                </div>
              )}
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForgotPasswordDialog(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordError('');
                  }}
                  disabled={isSendingOtp}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!forgotPasswordEmail || !forgotPasswordEmail.trim()) {
                      setForgotPasswordError('Please enter your email address');
                      return;
                    }
                    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(forgotPasswordEmail)) {
                      setForgotPasswordError('Please enter a valid email address');
                      return;
                    }
                    setIsSendingOtp(true);
                    setForgotPasswordError('');
                    try {
                      const response = await fetch(`${API_CONFIG.backendUrl}/api/auth/forgot-password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: forgotPasswordEmail.trim() }),
                      });
                      if (!response.ok) {
                        const errorText = await response.text();
                        setForgotPasswordError(errorText || 'Failed to send password reset code');
                        return;
                      }
                      // Success - show OTP dialog
                      setShowOtpDialog(true);
                    } catch (err) {
                      setForgotPasswordError(err instanceof Error ? err.message : 'Failed to send password reset code');
                    } finally {
                      setIsSendingOtp(false);
                    }
                  }}
                  disabled={isSendingOtp}
                >
                  {isSendingOtp ? 'Sending...' : 'Send Code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Dialog - OTP and New Password Input */}
      {showForgotPasswordDialog && showOtpDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowForgotPasswordDialog(false);
            setShowOtpDialog(false);
            setForgotPasswordEmail('');
            setOtp('');
            setNewPassword('');
            setConfirmNewPassword('');
            setOtpError('');
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Reset Password</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="otp" className="form-label">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="otp"
                  className="form-input"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setOtpError('');
                  }}
                  disabled={isResettingPassword}
                  placeholder="Enter 10-digit code"
                  maxLength={10}
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="newPassword" className="form-label">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setOtpError('');
                  }}
                  disabled={isResettingPassword}
                  placeholder="12-80 characters"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="confirmNewPassword" className="form-label">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmNewPassword"
                  className="form-input"
                  value={confirmNewPassword}
                  onChange={(e) => {
                    setConfirmNewPassword(e.target.value);
                    setOtpError('');
                  }}
                  disabled={isResettingPassword}
                  placeholder="Confirm password"
                  required
                />
              </div>
              {otpError && (
                <div className="error-message" style={{ marginTop: 0 }}>
                  {otpError}
                </div>
              )}
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowOtpDialog(false);
                    setOtp('');
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setOtpError('');
                  }}
                  disabled={isResettingPassword}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!otp || otp.length !== 10) {
                      setOtpError('Please enter the 10-digit verification code');
                      return;
                    }
                    if (!newPassword || newPassword.length < 12 || newPassword.length > 80) {
                      setOtpError('Password must be between 12 and 80 characters');
                      return;
                    }
                    if (newPassword !== confirmNewPassword) {
                      setOtpError('Passwords do not match');
                      return;
                    }
                    setIsResettingPassword(true);
                    setOtpError('');
                    try {
                      const response = await fetch(`${API_CONFIG.backendUrl}/api/auth/reset-password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          email: forgotPasswordEmail.trim(),
                          otp: otp,
                          password: newPassword,
                        }),
                      });
                      if (!response.ok) {
                        const errorText = await response.text();
                        setOtpError(errorText || 'Failed to reset password');
                        return;
                      }
                      // Success - close dialogs and show success message
                      setShowForgotPasswordDialog(false);
                      setShowOtpDialog(false);
                      setForgotPasswordEmail('');
                      setOtp('');
                      setNewPassword('');
                      setConfirmNewPassword('');
                      setOtpError('');
                      setSuccess('Password has been reset successfully! Please sign in.');
                    } catch (err) {
                      setOtpError(err instanceof Error ? err.message : 'Failed to reset password');
                    } finally {
                      setIsResettingPassword(false);
                    }
                  }}
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Username Dialog */}
      {showForgotUsernameDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowForgotUsernameDialog(false);
            setForgotUsernameEmail('');
            setForgotUsernameError('');
            setForgotUsernameSuccess('');
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Forgot Username</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="forgotUsernameEmail" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="forgotUsernameEmail"
                  className="form-input"
                  value={forgotUsernameEmail}
                  onChange={(e) => {
                    setForgotUsernameEmail(e.target.value);
                    setForgotUsernameError('');
                    setForgotUsernameSuccess('');
                  }}
                  disabled={isSendingUsername}
                  placeholder="Enter your email address"
                  required
                />
              </div>
              {forgotUsernameError && (
                <div className="error-message" style={{ marginTop: 0 }}>
                  {forgotUsernameError}
                </div>
              )}
              {forgotUsernameSuccess && (
                <div className="success-message" style={{ marginTop: 0 }}>
                  {forgotUsernameSuccess}
                </div>
              )}
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForgotUsernameDialog(false);
                    setForgotUsernameEmail('');
                    setForgotUsernameError('');
                    setSuccess('');
                  }}
                  disabled={isSendingUsername}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!forgotUsernameEmail || !forgotUsernameEmail.trim()) {
                      setForgotUsernameError('Please enter your email address');
                      return;
                    }
                    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(forgotUsernameEmail)) {
                      setForgotUsernameError('Please enter a valid email address');
                      return;
                    }
                    setIsSendingUsername(true);
                    setForgotUsernameError('');
                    setForgotUsernameSuccess('');
                    try {
                      const response = await fetch(`${API_CONFIG.backendUrl}/api/auth/forgot-username`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: forgotUsernameEmail.trim() }),
                      });
                      if (!response.ok) {
                        const errorText = await response.text();
                        setForgotUsernameError(errorText || 'Failed to send username');
                        return;
                      }
                      // Success
                      const successText = await response.text();
                      setSuccess(successText || 'If an account with this email exists, the username has been sent.');
                      setForgotUsernameEmail('');
                      setShowForgotUsernameDialog(false);
                    } catch (err) {
                      setForgotUsernameError(err instanceof Error ? err.message : 'Failed to send username');
                    } finally {
                      setIsSendingUsername(false);
                    }
                  }}
                  disabled={isSendingUsername}
                >
                  {isSendingUsername ? 'Sending...' : 'Send Username'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginScreen;

